
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export type MentoriaOffering = {
  id: string;
  title: string;
  description: string;
  price_cents: number;
  duration_minutes: number;
  is_group: boolean;
  max_participants: number;
  is_active: boolean;
  created_at: string;
  mentor_id: string;
  game_id: string;
  cover_image_url?: string | null;
  mentor?: {
    id: string;
    profile_id: string;
    profile?: {
      nickname: string;
    };
  };
  game?: {
    id: string;
    name: string;
  };
};

export type StatusFilter = 'all' | 'active' | 'inactive';

export const useMentoriasAdmin = () => {
  const [mentorias, setMentorias] = useState<MentoriaOffering[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentMentoria, setCurrentMentoria] = useState<MentoriaOffering | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const fetchMentorias = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("mentorship_offerings")
        .select(`
          *,
          mentor:mentor_id (
            id,
            profile_id,
            profile:profile_id (
              nickname
            )
          ),
          game:game_id (
            id,
            name
          )
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar mentorias:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar a lista de mentorias.",
          variant: "destructive",
        });
      } else {
        const mentoriasData = data || [];
        const activeCount = mentoriasData.filter(m => m.is_active).length;
        const inactiveCount = mentoriasData.filter(m => !m.is_active).length;
        console.log(`📊 [fetchMentorias] Mentorias carregadas:`, {
          total: mentoriasData.length,
          ativas: activeCount,
          inativas: inactiveCount
        });
        setMentorias(mentoriasData);
      }
    } catch (error) {
      console.error("Erro inesperado:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMentorias();
  }, []);

  const openEditDialog = (mentoria: MentoriaOffering) => {
    setIsEditMode(true);
    setCurrentMentoria(mentoria);
    setIsDialogOpen(true);
  };
  
  const openDetailsDialog = (mentoria: MentoriaOffering) => {
    setCurrentMentoria(mentoria);
    setIsDetailsDialogOpen(true);
  };

  const toggleMentoriaStatus = async (id: string, isActive: boolean) => {
    try {
      const newStatus = !isActive;
      console.log(`🔄 [toggleMentoriaStatus] Alternando status da mentoria ${id} de ${isActive} para ${newStatus}`);
      
      // Verificar se o usuário é admin antes de tentar atualizar
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("id, role")
          .eq("id", user.id)
          .single();
        
        console.log(`👤 [toggleMentoriaStatus] Usuário atual:`, {
          id: user.id,
          email: user.email,
          profile: profile,
          isAdmin: profile?.role === 'admin'
        });
        
        if (profileError) {
          console.error("❌ [toggleMentoriaStatus] Erro ao verificar perfil:", profileError);
        }
        
        if (profile?.role !== 'admin') {
          console.warn("⚠️ [toggleMentoriaStatus] Usuário não é admin! Role:", profile?.role);
          toast({
            title: "Erro",
            description: "Apenas administradores podem alterar o status de mentorias.",
            variant: "destructive",
          });
          return;
        }
      }
      
      // Usar função SQL que bypassa RLS
      const { data: functionResult, error: functionError } = await supabase.rpc(
        'admin_update_mentoria_status',
        {
          mentoria_id: id,
          novo_status: newStatus
        }
      );
      
      if (functionError) {
        console.error("❌ [toggleMentoriaStatus] Erro na função SQL:", functionError);
        throw functionError;
      }
      
      if (!functionResult || !functionResult.success) {
        console.error("❌ [toggleMentoriaStatus] Função retornou erro:", functionResult);
        throw new Error(functionResult?.error || "Erro ao atualizar status da mentoria");
      }
      
      console.log(`✅ [toggleMentoriaStatus] Função executada com sucesso:`, functionResult);
      
      // Buscar a mentoria atualizada para atualizar o estado local
      const { data, error: fetchError } = await supabase
        .from("mentorship_offerings")
        .select()
        .eq("id", id)
        .single();
      
      // Se não conseguiu buscar, usar o status que tentamos definir
      const updatedMentoria = data || { id, is_active: newStatus };
      
      if (fetchError) {
        console.error("❌ [toggleMentoriaStatus] Erro ao buscar mentoria atualizada:", fetchError);
        // Não falhar, apenas logar o erro - a atualização já foi feita pela função SQL
      }
      
      console.log(`✅ [toggleMentoriaStatus] Mentoria atualizada com sucesso:`, updatedMentoria);

      toast({
        title: "Sucesso",
        description: `Mentoria ${newStatus ? "ativada" : "desativada"} com sucesso!`,
      });
      
      // Atualizar o estado local imediatamente para feedback visual
      setMentorias(prev => prev.map(m => 
        m.id === id ? { ...m, is_active: newStatus } : m
      ));
      
      // Refresh mentorias list para garantir sincronização
      await fetchMentorias();
    } catch (error: any) {
      console.error("❌ [toggleMentoriaStatus] Erro ao alterar status da mentoria:", error);
      toast({
        title: "Erro",
        description: error?.message || "Ocorreu um erro ao alterar o status da mentoria. Verifique as políticas RLS ou permissões.",
        variant: "destructive",
      });
    }
  };

  const filteredMentorias = mentorias.filter(mentoria => {
    // Filtro por status
    const matchesStatus = statusFilter === 'all' 
      ? true 
      : statusFilter === 'active' 
        ? mentoria.is_active 
        : !mentoria.is_active;
    
    // Filtro por busca
    const matchesSearch = mentoria.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mentoria.mentor?.profile?.nickname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mentoria.game?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  return {
    mentorias: filteredMentorias,
    allMentorias: mentorias, // Todas as mentorias sem filtro
    loading,
    isDialogOpen,
    setIsDialogOpen,
    isDetailsDialogOpen,
    setIsDetailsDialogOpen,
    isEditMode,
    currentMentoria,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    openEditDialog,
    openDetailsDialog,
    toggleMentoriaStatus,
    fetchMentorias
  };
};
